import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import {
  EventColorCategory,
  ICalColorSet,
  ICalendarColorsMap,
} from "@clockwise/client-commons/src/util/event-category-coloring";
import {
  ConferencingType,
  EventPermissions,
  ResponseStatusEnum,
  TransparencyEnum,
} from "@clockwise/schema";
import {
  ConferencingType as GatewayConferencingType,
  ResponseStatusEnum as GatewayResponseStatusEnum,
} from "@clockwise/schema/v2";
import { formatEventTitle } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/formatEventTitle";
import { getEventStartEndTimeText } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/getEventStartEndTimeText";
import { getDateRange } from "@clockwise/web-commons/src/util/date.util";
import { IEventWithAnnotatedEvents } from "@clockwise/web-commons/src/util/event-category-coloring";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DateTime, Interval } from "luxon";
import { CalendarEventsQuery } from "../apollo/__generated__/CalendarEvents.generated";
import { CalendarEventsQuery as GatewayCalendarEventsQuery } from "../apollo/__generated__/CalendarEvents.v2.generated";
import { CalendarEvent, PlannerEventCardsByDay } from "../types";
import { getAttendeeResponseStatusFromEvent } from "./getAttendeeResponseStatusFromEvent";

// TODO (lsanwick) Remove this after converting to use Gateway types.
const toConferencingType = (gatewayType: GatewayConferencingType): ConferencingType => {
  switch (gatewayType) {
    case GatewayConferencingType.Meet:
      return ConferencingType.MEET;
    case GatewayConferencingType.TeamsForBusiness:
      return ConferencingType.TEAMS_FOR_BUSINESS;
    case GatewayConferencingType.Zoom:
      return ConferencingType.ZOOM;
    case GatewayConferencingType.Other:
      return ConferencingType.OTHER;
  }
};

// TODO (lsanwick) Remove this after converting PlannerEvent to have capability booleans.
const toEventPermissionsEnum = ({
  canRSVP,
  canSeeOtherGuests,
  canInviteOthers,
  canModify,
  canDelete,
}: {
  canRSVP: boolean;
  canSeeOtherGuests: boolean;
  canInviteOthers: boolean;
  canModify: boolean;
  canDelete: boolean;
}): EventPermissions => {
  if (canRSVP && canSeeOtherGuests && canInviteOthers && canModify && canDelete) {
    return EventPermissions.ALL;
  } else if (canRSVP && canSeeOtherGuests && canInviteOthers && canModify && !canDelete) {
    return EventPermissions.MODIFY;
  } else if (canRSVP && canSeeOtherGuests && canInviteOthers && !canModify && !canDelete) {
    return EventPermissions.INVITE;
  } else if (canRSVP && canSeeOtherGuests && !canInviteOthers && !canModify && !canDelete) {
    return EventPermissions.ISATTENDEE;
  } else {
    return EventPermissions.NONE;
  }
};

// TODO (lsanwick) Use this to convert the old event permissions to the new capability booleans,
// after migration.
export const toEventPermissions = (
  enumValue: EventPermissions,
): {
  canRSVP: boolean;
  canSeeOtherGuests: boolean;
  canInviteOthers: boolean;
  canModify: boolean;
  canDelete: boolean;
} => {
  switch (enumValue) {
    case EventPermissions.ALL:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: true,
        canModify: true,
        canDelete: true,
      };
    case EventPermissions.MODIFY:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: true,
        canModify: true,
        canDelete: false,
      };
    case EventPermissions.INVITE:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: true,
        canModify: false,
        canDelete: false,
      };
    case EventPermissions.ISATTENDEE:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: false,
        canModify: false,
        canDelete: false,
      };
    case EventPermissions.NONE:
      return {
        canRSVP: false,
        canSeeOtherGuests: false,
        canInviteOthers: false,
        canModify: false,
        canDelete: false,
      };
  }
};

function toWebserverResponseStatus(
  gatewayResponseStatus: GatewayResponseStatusEnum,
): ResponseStatusEnum {
  switch (gatewayResponseStatus) {
    case GatewayResponseStatusEnum.Accepted:
      return ResponseStatusEnum.Accepted;
    case GatewayResponseStatusEnum.Declined:
      return ResponseStatusEnum.Declined;
    case GatewayResponseStatusEnum.Tentative:
      return ResponseStatusEnum.Tentative;
    case GatewayResponseStatusEnum.NeedsAction:
      return ResponseStatusEnum.NeedsAction;
    case GatewayResponseStatusEnum.NotApplicable:
      return ResponseStatusEnum.NotApplicable;
  }
}

export function toPlannerEvents(
  data?: GatewayCalendarEventsQuery,
  colors?: ICalendarColorsMap,
): PlannerEventCardsByDay {
  return (
    data?.calendars.reduce<PlannerEventCardsByDay>((acc, member) => {
      member?.eventsByWeek.forEach((event) => {
        const timeInterval = Interval.fromISO(event.timeRange, { zone: getRenderTimeZone() });
        const date = timeInterval.start.toISODate();

        acc[date] = [
          ...(acc[date] ?? []),
          {
            active: false,
            annotaion: undefined,
            attendeeCount: event.attendees.length,
            attendeesCalendarIds: event.attendees.map((a) => a.id),
            calendarIds: [event.calendarId],
            deemphasis: false,
            eventCategory: (event.category.type as unknown) as EventColorCategory,
            eventCategoryColor: event.category.color ? colors?.[event.category.color] : undefined,
            eventPermissions: toEventPermissionsEnum(event.eventPermissions),
            externalEventId: event.externalEventId,
            flexStatus: null,
            interval: timeInterval,
            isAllDay: event.isAllDay,
            isCancelled: event.isCanceled,
            key: event.id,
            locked: event.isLocked,
            responseState: toWebserverResponseStatus(event.responseStatus),
            subText: getEventStartEndTimeText(timeInterval.start, timeInterval.end),
            text: event.title,
            transparency: event.isMarkedBusy
              ? TransparencyEnum.Opaque
              : TransparencyEnum.Transparent,
            isCollapseable: event.isCollapsible,
            videoLink: event.conferencing
              ? {
                  __typename: "VideoLink",
                  uri: event.conferencing.url,
                  type: toConferencingType(event.conferencing.type),
                }
              : null,
          },
        ];
      });
      return acc;
    }, {}) ?? {}
  );
}

export const parseAICalendarEventsDataGQL = (
  calendarData: CalendarEventsQuery,
  weeksToProcess: string[],
  eventColorGetter: (event: IEventWithAnnotatedEvents) => ICalColorSet | null,
  eventCategoryGetter: (event: IEventWithAnnotatedEvents) => EventColorCategory | null | undefined,
): [PlannerEventCardsByDay, CalendarEvent[]] => {
  const newEvents: CalendarEvent[] = [];
  const newCardsByDay: PlannerEventCardsByDay = {};
  if (calendarData && getValue(calendarData.org)) {
    const calendarEvents = getValue(
      getValue(calendarData?.org, "Org")?.eventParentBaseWrapperByWeeksObject,
    )?.list;

    // Iterate over all events, within each eventParents, within each eventParentsByDay, and push into eventsByDay map.
    calendarEvents?.forEach?.((week) => {
      week.eventParentsByDay.forEach((byDay) => {
        byDay.eventParents?.forEach((eventParent) => {
          eventParent.events.forEach((e) => {
            getDateRange(
              DateTime.fromISO(e.startTime.dateOrDateTime, {
                zone: getRenderTimeZone(),
              }).toISODate(),
              DateTime.fromISO(e.endTime.dateOrDateTime, { zone: getRenderTimeZone() })
                .minus({ seconds: 1 })
                .toISODate(),
            ).forEach((dateISO) => {
              // Only process events that occur on weeksToProcess. Only process these week as
              // all day events or events that span into prior or next week can be present here, but
              // other events in that week may not be present.
              // For example, the prefetch call that fetches the week after the one being currently viewed may have an
              // all day event that spans into the week being viewed, and if we just processed each day that that event spans, we would
              // call setEventsByDay down at the bottom of this function with only that all day event, and not the events current being displayed.
              // We can't just merge the two arrays, as its a valid use-case for an event to be deleted an not appear in a followup fetch of a week.
              if (
                !weeksToProcess
                  .flatMap((w) => getDateRange(w, DateTime.fromISO(w).plus({ days: 6 }).toISO()))
                  .includes(dateISO)
              ) {
                return;
              }
              // Check if event exists in map already, if so skip adding to startDate.
              if (
                newCardsByDay[dateISO]?.find(
                  (event) => event.externalEventId === e.eventKey.externalEventId,
                )
              ) {
                return; // All day events show up each day they span, so skip adding to startDate if already have.
              }

              newEvents.push(e);

              const eventCategory = eventCategoryGetter(e);
              const eventCategoryColor = eventColorGetter(e);

              const dateConfig = { zone: getRenderTimeZone() };
              const currentDate = DateTime.fromISO(dateISO, dateConfig);
              const startDateTime = DateTime.fromISO(e.startTime.dateOrDateTime, dateConfig);
              const endDateTime = DateTime.fromISO(e.endTime.dateOrDateTime, dateConfig);
              const subText = getEventStartEndTimeText(startDateTime, endDateTime);
              const responseStatus = getAttendeeResponseStatusFromEvent(e, e.calendarIds[0]);

              const dayInterval = Interval.fromDateTimes(
                DateTime.max(startDateTime, currentDate.startOf("day")),
                DateTime.min(endDateTime, currentDate.endOf("day")),
              );

              const eventInterval = Interval.fromDateTimes(startDateTime, endDateTime);

              newCardsByDay[dateISO] = [
                ...(newCardsByDay[dateISO] || []),
                {
                  active: false,
                  annotaion: undefined,
                  attendeeCount: e.attendees.length,
                  attendeesCalendarIds: e.attendees.map((a) => a.urnValue),
                  calendarIds: e.calendarIds,
                  deemphasis: false,
                  eventCategory: eventCategory ?? undefined,
                  eventCategoryColor: eventCategoryColor ?? undefined,
                  eventPermissions: e.eventPermissions,
                  externalEventId: e.eventKey.externalEventId,
                  flexStatus: null,
                  interval: e.isAllDay ? eventInterval : dayInterval, // All day multi-day and interday all day events need to manage interval differently.
                  isAllDay: !!e.isAllDay,
                  isCancelled: !!e.isCancelled,
                  isCollapseable:
                    e.isClockwiseEvent &&
                    e.title === "❇️ Focus Time (via Clockwise)" &&
                    eventParent.smartHoldMatch?.__typename === "FocusTimeHold" &&
                    eventParent.smartHoldMatch.state?.__typename !== "Paused" &&
                    eventParent.smartHoldMatch.state?.__typename !== "Protected",
                  key: `${e.eventKey.externalEventId}-${dateISO}`,
                  keyAddendum: e.calendarIds[0],
                  locked: e.locked,
                  responseState: responseStatus || ResponseStatusEnum.NotApplicable,
                  subText,
                  text: formatEventTitle(e.title, e.locked),
                  transparency: e.transparency,
                  videoLink: e.videoLink,
                },
              ];
            });
          });
        });
      });
    });
  }

  return [newCardsByDay, newEvents];
};
